import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  MessageSquare,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronRight,
  Ticket,
  RefreshCw
} from 'lucide-react';
import { apiRequest } from '../../api/client';
import { useI18n } from '../../i18n';

const getStatusConfig = (t) => ({
  open: { label: t('support.statuses.open'), color: 'text-blue-400', bg: 'bg-blue-500/10', icon: AlertCircle },
  in_progress: { label: t('support.statuses.in_progress'), color: 'text-yellow-400', bg: 'bg-yellow-500/10', icon: Loader2 },
  waiting_customer: { label: t('support.statuses.waiting_customer'), color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Clock },
  waiting_internal: { label: t('support.statuses.waiting_internal'), color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Clock },
  resolved: { label: t('support.statuses.resolved'), color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle },
  closed: { label: t('support.statuses.closed'), color: 'text-gray-400', bg: 'bg-gray-500/10', icon: XCircle }
});

const getPriorityConfig = (t) => ({
  low: { label: t('support.priorities.low'), color: 'text-gray-400', bg: 'bg-gray-500/10' },
  medium: { label: t('support.priorities.medium'), color: 'text-blue-400', bg: 'bg-blue-500/10' },
  high: { label: t('support.priorities.high'), color: 'text-orange-400', bg: 'bg-orange-500/10' },
  urgent: { label: t('support.priorities.urgent'), color: 'text-red-400', bg: 'bg-red-500/10' }
});

const getCategoryConfig = (t) => ({
  technical: { label: t('support.categories.technical'), color: 'text-cyan-400' },
  billing: { label: t('support.categories.billing'), color: 'text-green-400' },
  feature_request: { label: t('support.categories.feature_request'), color: 'text-purple-400' },
  bug_report: { label: t('support.categories.bug_report'), color: 'text-red-400' },
  account: { label: t('support.categories.account'), color: 'text-blue-400' },
  security: { label: t('support.categories.security'), color: 'text-yellow-400' },
  other: { label: t('support.categories.other'), color: 'text-gray-400' }
});

const TicketsList = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const statusConfig = getStatusConfig(t);
  const priorityConfig = getPriorityConfig(t);
  const categoryConfig = getCategoryConfig(t);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = '/api/v1/support/tickets?limit=50';
      if (statusFilter) {
        url += `&status_filter=${statusFilter}`;
      }

      const data = await apiRequest(url);
      setTickets(data || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(t('support.errors.loadingTickets'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTickets();
    setRefreshing(false);
  };

  const filteredTickets = tickets.filter(ticket => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ticket.ticket_number?.toLowerCase().includes(search) ||
      ticket.subject?.toLowerCase().includes(search) ||
      ticket.description?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Ticket className="w-8 h-8 text-brand-green" />
            {t('support.myTickets')}
          </h1>
          <p className="text-gray-400 mt-1">{t('support.manageRequests')}</p>
        </div>

        <Link
          to="/support/tickets/new"
          className="flex items-center gap-2 px-6 py-3 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-all"
        >
          <Plus className="w-5 h-5" />
          {t('support.newTicket')}
        </Link>
      </div>

      {/* Filters */}
      <div className="liquid-glass rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('support.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-dark-800/50 border border-brand-green/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2.5 bg-dark-800/50 border border-brand-green/20 rounded-lg text-white focus:outline-none focus:border-brand-green/50 appearance-none cursor-pointer min-w-[200px]"
            >
              <option value="">{t('support.allStatuses')}</option>
              {Object.entries(statusConfig).map(([value, { label }]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2.5 bg-dark-800/50 border border-brand-green/20 rounded-lg text-gray-400 hover:text-brand-green hover:border-brand-green/50 transition-all"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">{t('support.refresh')}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      ) : error ? (
        <div className="liquid-glass rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
          <button
            onClick={fetchTickets}
            className="mt-4 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-all"
          >
            {t('support.errors.retry')}
          </button>
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="liquid-glass rounded-xl p-12 text-center">
          <Ticket className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {searchTerm || statusFilter ? t('support.noTicketsFound') : t('support.noTickets')}
          </h3>
          <p className="text-gray-400 mb-6">
            {searchTerm || statusFilter
              ? t('support.tryModifyingSearch')
              : t('support.noTicketsYet')}
          </p>
          {!searchTerm && !statusFilter && (
            <Link
              to="/support/tickets/new"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-all"
            >
              <Plus className="w-5 h-5" />
              {t('support.createFirstTicket')}
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket) => {
            const status = statusConfig[ticket.status] || statusConfig.open;
            const priority = priorityConfig[ticket.priority] || priorityConfig.medium;
            const category = categoryConfig[ticket.category] || categoryConfig.other;
            const StatusIcon = status.icon;

            return (
              <Link
                key={ticket.id}
                to={`/support/tickets/${ticket.id}`}
                className="block liquid-glass rounded-xl p-5 hover:border-brand-green/30 transition-all group"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Main Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-brand-green font-mono text-sm">
                        {ticket.ticket_number}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${status.bg} ${status.color}`}>
                        <StatusIcon className="w-3 h-3 inline mr-1" />
                        {status.label}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${priority.bg} ${priority.color}`}>
                        {priority.label}
                      </span>
                    </div>

                    <h3 className="text-lg font-semibold text-white group-hover:text-brand-green transition-colors truncate">
                      {ticket.subject}
                    </h3>

                    <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                      {ticket.description}
                    </p>

                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className={category.color}>{category.label}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(ticket.created_at)}
                      </span>
                      {ticket.messages_count > 0 && (
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {ticket.messages_count} {ticket.messages_count > 1 ? t('support.messagesPlural') : t('support.messages')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-brand-green transition-colors hidden md:block" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TicketsList;
